import JSEncrypt from 'jsencrypt';
import { ConfigService } from './config-service';

class EncrDecrService {
  private static instance: EncrDecrService;
  private publicKey: any = {
    encryptAlgo: {},
    verifyAlgo: {}
  };

  static getInstance(): EncrDecrService {
    if (!EncrDecrService.instance) {
      EncrDecrService.instance = new EncrDecrService();
    }
    return EncrDecrService.instance;
  }

  async setPublicKeyPem(pem: string): Promise<void> {
    try {
      if (typeof pem !== 'string') {
        this.publicKey = pem;
        return;
      }
      const pemContents = pem
        .replace(/-----BEGIN PUBLIC KEY-----/, '')
        .replace(/-----END PUBLIC KEY-----/, '')
        .replace(/\s/g, '');
      const binaryDerString = window.atob(pemContents);
      const binaryDer = new Uint8Array([...binaryDerString].map(char => char.charCodeAt(0)));
      const verifyAlgo = await crypto.subtle.importKey(
        'spki',
        binaryDer.buffer,
        {
          name: 'RSASSA-PKCS1-v1_5',
          hash: { name: 'SHA-256' }
        },
        true,
        ['verify']
      );

      this.publicKey = {
        verifyAlgo: verifyAlgo
      };
      console.log('Public key set successfully');
    } catch (error) {
      console.error('Error setting public key:', error);
    }
  }

  async set(dataToEncrypt: string): Promise<string | null> {
    try {
      const aesKey = await crypto.subtle.generateKey({ name: 'AES-CBC', length: 256 }, true, ['encrypt', 'decrypt']);
      const iv = crypto.getRandomValues(new Uint8Array(16));

      const encodedData = new TextEncoder().encode(dataToEncrypt);
      const encryptedData = await crypto.subtle.encrypt({ name: 'AES-CBC', iv }, aesKey, encodedData);

      const rawAesKey = await crypto.subtle.exportKey('raw', aesKey);

      const rawAesKeyBase64 = this.arrayBufferToBase64(rawAesKey);

      const jsEncrypt = new JSEncrypt();
      jsEncrypt.setPublicKey(ConfigService.encryption.encryptionKey);
      const encryptedAesKey = jsEncrypt.encrypt(rawAesKeyBase64);

      if (!encryptedAesKey) {
        throw new Error('RSA encryption (PKCS1 v1.5) failed');
      }

      const result = {
        encryptedData: this.arrayBufferToBase64(encryptedData),
        encryptedAesKey: encryptedAesKey, // already base64 encoded by jsencrypt
        iv: this.arrayBufferToBase64(iv.buffer)
      };

      return JSON.stringify(result);
    } catch (error) {
      console.error('Encryption error:', error);
      return null;
    }
  }

  async get(value: { data: string; signature: string }): Promise<string | null> {
    const isValid = await this.verify(value.data, value.signature);
    if (!isValid) {
      console.warn('Invalid Signature. Data may be tampered.');
      return null;
    }

    if (this.isValidJSON(value.data)) {
      value.data = JSON.parse(value.data);
    } else {
      console.warn('Data is not valid JSON');
      return null;
    }
    return value.data;
  }

  isValidJSON(value: string): boolean {
    try {
      JSON.parse(value);
      return true;
    } catch (e) {
      return false;
    }
  }
  async verify(data: string, signatureBase64: string): Promise<boolean> {
    try {
      const signature = this.base64ToArrayBuffer(signatureBase64);
      const encoded = new TextEncoder().encode(data);

      const result = await crypto.subtle.verify(
        {
          name: 'RSASSA-PKCS1-v1_5',
          hash: { name: 'SHA-256' }
        },
        this.publicKey.verifyAlgo,
        signature,
        encoded
      );

      return result;
    } catch (error) {
      console.error('Verification error:', error);
      return false;
    }
  }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let b of bytes) {
      binary += String.fromCharCode(b);
    }
    return window.btoa(binary);
  }

  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binary = window.atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
  }
}

export const EncrDecr = EncrDecrService.getInstance();
